import React, { useState, useEffect } from 'react';
import { FiAward, FiCode, FiUsers, FiCpu, FiSave, FiCheck, FiFileText } from 'react-icons/fi';
import ImageUploadField from './ImageUploadField';
import './AdminManage.css';

const STORAGE_KEY_CHALLENGE = 'iamar_challenge_content';

const DEFAULT_CHALLENGE_DATA = {
  doctoral: {
    title: "Compétition Doctorale Internationale",
    subtitle: "Valorisation des thèses et travaux de recherche doctorale en gouvernance sécuritaire",
    deadline: "15 Septembre 2026",
    image: "",
    description: "Offrir aux jeunes chercheurs et doctorants une tribune d'excellence pour présenter leurs avancées scientifiques devant un jury international prestigieux."
  },
  awards: {
    title: "GSIDA Awards 2026",
    subtitle: "Distinction de l'excellence académique et de l'innovation opérationnelle",
    categories: "Prix de la Meilleure Thèse, Prix de l'Innovation Numérique Sécuritaire, Prix Spécial du Jury",
    image: ""
  },
  hackathon: {
    title: "Hackathon Sécurité & IA",
    subtitle: "48 Heures pour coder l'avenir de la résilience numérique",
    dates: "29 - 30 Octobre 2026",
    image: "",
    description: "Challenge technologique invitant développeurs, data scientists et experts en sécurité à concevoir des prototypes opérationnels d'IA appliquée."
  },
  publicInnovation: {
    title: "Innovation Publique & Territoires",
    subtitle: "Transformer l'action publique par des solutions pragmatiques et concertées",
    image: "",
    description: "Mettre en avant les meilleures initiatives de modernisation des services publics et de gouvernance territoriale."
  }
};

export default function AdminAcademicChallenge() {
  const [activeTab, setActiveTab] = useState('doctoral');
  const [data, setData] = useState(DEFAULT_CHALLENGE_DATA);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_CHALLENGE);
      if (saved) {
        setData(JSON.parse(saved));
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY_CHALLENGE, JSON.stringify(data));
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2500);
  };

  const updateSub = (key, field, val) => {
    setData(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: val
      }
    }));
  };

  const tabs = [
    { id: 'doctoral', label: "Compétition Doctorale", icon: <FiFileText /> },
    { id: 'awards', label: "GSIDA Awards", icon: <FiAward /> },
    { id: 'hackathon', label: "Hackathon", icon: <FiCode /> },
    { id: 'publicInnovation', label: "Innovation Publique", icon: <FiCpu /> }
  ];

  return (
    <div className="admin-manage-card" style={{ background: '#ffffff', borderRadius: 8, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: 16, marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.4rem', color: '#1b1e4f', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiAward /> GSIDA Academic Challenge
          </h2>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.88rem' }}>
            Gestion des défis académiques : Compétition Doctorale, GSIDA Awards, Hackathon et Innovation Publique
          </p>
        </div>

        <button
          onClick={handleSave}
          style={{
            background: saveSuccess ? '#10b981' : '#1b1e4f',
            color: 'white',
            border: 'none',
            padding: '9px 20px',
            borderRadius: 6,
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          {saveSuccess ? <FiCheck size={16} /> : <FiSave size={16} />}
          {saveSuccess ? 'Enregistré avec succès !' : 'Enregistrer les modifications'}
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 24, padding: 6, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              border: 'none',
              background: activeTab === t.id ? '#1b1e4f' : 'transparent',
              color: activeTab === t.id ? '#ffffff' : '#475569',
              padding: '8px 14px',
              borderRadius: 6,
              fontSize: 12.5,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              transition: 'all 0.15s ease'
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Tab 1: Compétition Doctorale */}
      {activeTab === 'doctoral' && (
        <div style={{ maxWidth: 840 }}>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', marginBottom: 16 }}>
            Compétition Doctorale
          </h3>
          <ImageUploadField
            label="Affiche ou visuel de la compétition (Upload image)"
            value={data.doctoral.image}
            onChange={(url) => updateSub('doctoral', 'image', url)}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Titre</label>
              <input
                type="text"
                value={data.doctoral.title}
                onChange={(e) => updateSub('doctoral', 'title', e.target.value)}
                style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Date limite de soumission</label>
              <input
                type="text"
                value={data.doctoral.deadline}
                onChange={(e) => updateSub('doctoral', 'deadline', e.target.value)}
                style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
              />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Sous-titre / Objectif</label>
            <input
              type="text"
              value={data.doctoral.subtitle}
              onChange={(e) => updateSub('doctoral', 'subtitle', e.target.value)}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Description & Modalités</label>
            <textarea
              rows={4}
              value={data.doctoral.description}
              onChange={(e) => updateSub('doctoral', 'description', e.target.value)}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
        </div>
      )}

      {/* Tab 2: GSIDA Awards */}
      {activeTab === 'awards' && (
        <div style={{ maxWidth: 840 }}>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', marginBottom: 16 }}>
            GSIDA Awards
          </h3>
          <ImageUploadField
            label="Visuel ou trophée des Awards (Upload image)"
            value={data.awards.image}
            onChange={(url) => updateSub('awards', 'image', url)}
          />
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Titre des Prix</label>
            <input
              type="text"
              value={data.awards.title}
              onChange={(e) => updateSub('awards', 'title', e.target.value)}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Catégories de distinctions</label>
            <textarea
              rows={3}
              value={data.awards.categories}
              onChange={(e) => updateSub('awards', 'categories', e.target.value)}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
        </div>
      )}

      {/* Tab 3: Hackathon */}
      {activeTab === 'hackathon' && (
        <div style={{ maxWidth: 840 }}>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', marginBottom: 16 }}>
            Hackathon Sécurité & IA
          </h3>
          <ImageUploadField
            label="Bannière du Hackathon (Upload image)"
            value={data.hackathon.image}
            onChange={(url) => updateSub('hackathon', 'image', url)}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Titre de l'épreuve</label>
              <input
                type="text"
                value={data.hackathon.title}
                onChange={(e) => updateSub('hackathon', 'title', e.target.value)}
                style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Dates du Hackathon</label>
              <input
                type="text"
                value={data.hackathon.dates}
                onChange={(e) => updateSub('hackathon', 'dates', e.target.value)}
                style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
              />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Règlement & Objectifs</label>
            <textarea
              rows={4}
              value={data.hackathon.description}
              onChange={(e) => updateSub('hackathon', 'description', e.target.value)}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
        </div>
      )}

      {/* Tab 4: Public Innovation */}
      {activeTab === 'publicInnovation' && (
        <div style={{ maxWidth: 840 }}>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', marginBottom: 16 }}>
            Innovation Publique & Souveraineté
          </h3>
          <ImageUploadField
            label="Illustration du volet Innovation Publique (Upload image)"
            value={data.publicInnovation.image}
            onChange={(url) => updateSub('publicInnovation', 'image', url)}
          />
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Titre</label>
            <input
              type="text"
              value={data.publicInnovation.title}
              onChange={(e) => updateSub('publicInnovation', 'title', e.target.value)}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Description & Projets ciblés</label>
            <textarea
              rows={4}
              value={data.publicInnovation.description}
              onChange={(e) => updateSub('publicInnovation', 'description', e.target.value)}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
